import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Shield, Eye, Share2 } from 'lucide-react';

const AboutSection = () => {
  const { t, language } = useLanguage();

  const features = [
    {
      icon: Shield,
      title: t('protection'),
      description: t('protectionDesc'),
      id: '01',
      tag: 'DEFENSE',
    },
    {
      icon: Eye,
      title: t('monitoring'),
      description: t('monitoringDesc'),
      id: '02',
      tag: 'DETECTION',
    },
    {
      icon: Share2,
      title: t('sharing'),
      description: t('sharingDesc'),
      id: '03',
      tag: 'INTELLIGENCE',
    },
  ];

  const sectionMeta = language === 'tr' ? '// BELGELER · R-01' : '// DOCUMENTATION · R-01';
  const aboutLabel = language === 'tr' ? 'HAKKINDA · 2 / 2' : 'ABOUT · 2 / 2';

  return (
    <section className="py-16" data-testid="about-section">
      {/* Section heading */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary">
            {sectionMeta}
          </span>
          <span className="flex-1 h-px bg-border" />
          <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
            {aboutLabel}
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
          {t('whatIsRTBH')}
        </h2>
        <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-3xl leading-relaxed">
          {t('aboutDescription')}
        </p>
      </div>

      {/* Feature grid */}
      <div className="grid md:grid-cols-3 gap-0 md:gap-[-1px] border border-border bg-card mb-8">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <div
              key={index}
              className={`relative p-6 group hover:bg-muted/30 transition-colors ${index < features.length - 1 ? 'md:border-r border-border' : ''}`}
              data-testid={`feature-card-${index}`}
            >
              {/* Top meta */}
              <div className="flex items-center justify-between mb-5">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  {feature.id} · {feature.tag}
                </span>
                <span className="status-dot" />
              </div>

              {/* Icon */}
              <div className="inline-flex items-center justify-center w-11 h-11 border border-primary/40 bg-primary/10 mb-4 sharp group-hover:bg-primary/20 transition-colors">
                <Icon className="h-5 w-5 text-primary" strokeWidth={2.25} />
              </div>

              <h3 className="text-lg font-semibold mb-2 text-foreground">{feature.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
            </div>
          );
        })}
      </div>

      {/* Text panels */}
      <div className="grid md:grid-cols-2 gap-4">
        {[t('aboutText1'), t('aboutText2')].map((text, i) => (
          <div key={i} className="border border-border bg-card">
            <div className="flex items-center justify-between px-5 py-2 border-b border-border bg-muted/30">
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                // DOC.{String(i + 1).padStart(2, '0')}
              </span>
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-primary">
                PUBLIC
              </span>
            </div>
            <div className="p-5">
              <p className="text-muted-foreground leading-relaxed text-sm">
                {text}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AboutSection;
